/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  useTheme,
  FormControl,
  Autocomplete,
  InputLabel,
  Select,
  MenuItem,
  ThemeProvider,
  CssBaseline,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import { Add, Cancel, Delete, Edit, Save } from "@mui/icons-material";
import { Logger } from "utils/Logger";
import { DataSet, TestStep as TestStep_Structure } from "../../Interfaces/AutomateTest";
import {
  CREATE_TESTSTEP_URL,
  DELETE_TESTSTEP_URL,
  GET_ALL_DATASETS_URL,
  GET_ALL_TESTSTEP_URL,
  UPDATE_STEP_URL,
} from "envConfig";

export const TestStep: React.FC = () => {
  const { testSuiteId, testCaseId } = useParams();
  const [testSteps, setTestSteps] = useState<TestStep_Structure[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentTestStep, setCurrentTestStep] = useState<Partial<TestStep_Structure> | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const { tenantId } = useParams<{ tenantId: string }>();
  const [dataSets, setDataSets] = useState<DataSet[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  // const [dataSetId, setDataSetId] = useState<string | null>(null);

  const theme = useTheme();

  // Fetch TestSteps
  const fetchTestSteps = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${GET_ALL_TESTSTEP_URL}/${testSuiteId}/${testCaseId}`, {
        method: "GET",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      if (!response.ok){
        setLoading(false);
        throw new Error(`Error: ${response.status}`);
      }
      const data = await response.json();
      Logger.info("pg-qa-portal-fe/src/modules/AutomateTest/TestStep/TestStep.tsx : TestSteps:", JSON.stringify(data.data, null, 2))
      setTestSteps(Array.isArray(data.data) ? data.data : []);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      Logger.error("Failed to fetch test steps:", error);
    }
  };

  useEffect(() => {
    fetchTestSteps();
  }, []);

  // Open Dialog for Add/Edit
  const openDialog = (testStep?: TestStep_Structure) => {
    setCurrentTestStep(
      testStep || {
        stepId: testSteps.reduce((max, ts) => Math.max(max, ts.stepId), 0) + 1,
        name: "",
        description: "",
        action: "",
        dataKeyFromDataSetForAction: null,
        elementKeyFromDataSet: null,
        elementKeyTypeFromDataSet: null,
        elementSelectorValue: null,
        elementSelectorType: null,
        dataValueForAction: null,
        status: "Pending",
        screenshotsLocation: "path",
        error:"no error",
        failOnError: false,
      }
    );
    setIsEditing(!!testStep);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setCurrentTestStep(null);
  };

  // Save TestStep (Add or Update)
  const saveTestStep = async () => {
    if (!currentTestStep) return;
    try {
      setLoading(true);
      const url = isEditing
        ? `${UPDATE_STEP_URL}/${testSuiteId}/${testCaseId}/${currentTestStep._id}`
        : `${CREATE_TESTSTEP_URL}/${testSuiteId}/${testCaseId}`;
      const method = isEditing ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(currentTestStep),
      });

      if (!response.ok){
         setLoading(false);
         throw new Error(`Error: ${response.statusText}`);
      }
      await fetchTestSteps();
      closeDialog();
      setLoading(false);
    } catch (error) {
      setLoading(false);
      Logger.error("Failed to save test step:", error);
    }
  };

  // Delete TestStep
  const deleteTestStep = async (testStepId: string) => {
    try {
      setLoading(true);
      const response = await fetch(`${DELETE_TESTSTEP_URL}/${testSuiteId}/${testCaseId}/${testStepId}`, {
        method: "DELETE",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      if (!response.ok){
          setLoading(false);
          throw new Error(`Error: ${response.status}`);
        }
      await fetchTestSteps();
      setLoading(false);
    } catch (error) {
      setLoading(false);
      Logger.error("Failed to delete test step:", error);
    }
  };

  const fetchDataSets = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${GET_ALL_DATASETS_URL}/${tenantId}`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        setLoading(false);
        throw new Error(`Error: ${response.status}`);
      }

              const data = await response.json();
        if (Array.isArray(data.data)) {
            setDataSets(data.data);
            setLoading(false);
            Logger.debug("st-ck-client/src/modules/AutomateTest/TestStep/TestStep.ts : fetchDataSets(): ", `DataSets: ${data.data.length} items`);
        } else {
          setDataSets([]);
          setLoading(false);
          Logger.debug("st-ck-client/src/modules/AutomateTest/TestStep/TestStep.ts : fetchDataSets(): Expected an array, received:", data.data);
        }

    } catch (error) {
      setDataSets([]);
      setLoading(false);
        Logger.debug("st-ck-client/src/modules/AutomateTest/TestStep/TestStep.ts : fetchDataSets(): ", "Failed to fetch DataSets:"+ error);
    }
  };
  useEffect(() => {
    fetchDataSets();
  }, []);

  
  return (loading ?
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Backdrop
        sx={(theme) => ({ color: theme.palette.secondary.main, zIndex: theme.zIndex.drawer + 1 })}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </ThemeProvider>
    :
    <Box pl={{ xs: 1.25, sm: 2.5 }} pt={1} width="100%">
      <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
      {/* <BreadcrumbNav /> */}
        <Typography variant="h4" gutterBottom>
          All TestSteps
        </Typography>
        <Button
          variant="contained"
          sx={{ backgroundColor: theme.palette.primary.main }}
          startIcon={<Add />}
          onClick={() => openDialog()}
        >
          New TestStep
        </Button>
      </Box>
      <TableContainer component={Paper} sx={{ maxHeight: "550px", overflow: "auto" }}>
        <Table sx={{ tableLayout: "auto", "& td, & th": { maxWidth: "130px", width:"150px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}}>
          <TableHead>
            <TableRow>
              <TableCell>STEP ID</TableCell>
              <TableCell>NAME</TableCell>
              <TableCell>DESC</TableCell>
              <TableCell>ACTION</TableCell>
              <TableCell>DATA SET</TableCell>
              <TableCell>DATA KEY</TableCell>
              <TableCell>FAIL ON ERROR</TableCell>
              <TableCell>ACTIONS</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {testSteps.map((testStep) => (
              <TableRow key={testStep._id}>
                <TableCell>{testStep.stepId}</TableCell>
                <TableCell>{testStep.name}</TableCell>
                <TableCell>{testStep.description}</TableCell>
                <TableCell>{testStep.action}</TableCell>
                <TableCell>{"DataSet " +testStep?.dataSetId || "--"}</TableCell>
                <TableCell>{testStep?.dataKeyFromDataSetForAction || "--"}</TableCell>
                <TableCell>{testStep?.failOnError ? "Yes": "No"}</TableCell>
                <TableCell>
                  <IconButton onClick={() => openDialog(testStep)}>
                    <Edit />
                  </IconButton>
                  <IconButton onClick={() => deleteTestStep(testStep._id)}>
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog for Add/Edit */}
      <Dialog  open={dialogOpen} onClose={closeDialog} fullWidth>
        <DialogTitle>{isEditing ? "Edit TestStep" : "Add New TestStep"}</DialogTitle>
        <DialogContent>
        {Object.keys(currentTestStep || {}).filter((key) => key !== "_id" && key !== "dataSetId" 
            && key !== "elementKeyFromDataSet" && key !== "failOnError" && key !== "status" && key !== "error" && key !== "screenshotsLocation").map((key) => (
            <TextField size="small" key={key} label={key} value={(currentTestStep as any)[key] || ""}
              onChange={(e) => setCurrentTestStep((prev) => prev ? { ...prev, [key]: e.target.value } : null)}
              fullWidth margin="normal" disabled={
                key === "stepId" || (!!currentTestStep?.dataSetId && ["elementSelectorValue", "elementSelectorType"].includes(key)) 
              }
              required={["action", "failOnError"].includes(key)}
              error={["action", "failOnError", "name", ].includes(key) && !(currentTestStep as any)[key]}
              helperText={["action", "failOnError"].includes(key) && !(currentTestStep as any)[key] && `${key} is required`}
            />
          ))}

          {/* Dataset ID Dropdown */}
        <FormControl fullWidth margin="normal">
          <Autocomplete size="small" options={dataSets} 
              getOptionLabel={(option) => String(option?.dataSetId)}
              value={dataSets.find((dataSet) => dataSet.dataSetId === currentTestStep?.dataSetId) || null}
              onChange={(_, newValue) => setCurrentTestStep((prev) => (prev ? { ...prev, "dataSetId": newValue?.dataSetId } : null))}
              renderInput={(params) => (<TextField {...params} label="Select DataSet" />)}
          />
        </FormControl>
        
        {/* Data Key Dropdown */}
        <FormControl fullWidth margin="normal" >
          <Autocomplete size="small" options={dataSets.find((dataSet) => dataSet.dataSetId === currentTestStep?.dataSetId)?.data ?? []} 
              getOptionLabel={(option) => option.key}
              value={dataSets.find((dataSet) => dataSet.dataSetId === currentTestStep?.dataSetId)?.data.find((key) => key === currentTestStep) || null}
              onChange={(_, newValue) => setCurrentTestStep((prev) => (prev ? { ...prev, "elementKeyFromDataSet": newValue?.key,
                 "elementKeyTypeFromDataSet": dataSets.find((dataSet) => dataSet.dataSetId === currentTestStep?.dataSetId)?.data.find((data) => data.key ===newValue?.key)?.elementKeyTypeFromDataSet,
                "dataKeyFromDataSetForAction": dataSets.find((dataSet) => dataSet.dataSetId === currentTestStep?.dataSetId)?.data.find((data) => data.key ===newValue?.key)?.value} : null))}
              renderInput={(params) => (<TextField {...params} label="Select DataKey" />)}
          />
        </FormControl >
        <FormControl fullWidth margin="normal">
        <InputLabel>Fail on Error</InputLabel>
        <Select value={currentTestStep?.failOnError ?? false}
          onChange={(e) => setCurrentTestStep((prev) => ({ ...prev, "failOnError": e.target.value || false}))}
          label="Select Value"
          size="small"
        >
          <MenuItem value="true">True</MenuItem>
          <MenuItem value="false">False</MenuItem>
        </Select>
      </FormControl>
        </DialogContent>
        <DialogActions>
          <Button sx={{ backgroundColor: "primary.light" }} onClick={closeDialog} startIcon={<Cancel />}>
            Cancel
          </Button>
          <Button onClick={saveTestStep} startIcon={<Save />} variant="contained" 
          // disabled={
          //  !["action", "dataSetId"].every((field) => (currentTestStep as any)[field])}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

{/* <Dialog open={dialogOpen} onClose={closeDialog} fullWidth>
      <DialogTitle>{isEditing ? "Edit TestStep" : "Add New TestStep"}</DialogTitle>
      <DialogContent>
        {Object.keys(currentTestStep || {}).filter((key) => key !== "_id" && key !== "dataSetId" && key !== "dataKeyFromDataSet").map((key) => (
          <TextField
            key={key}
            label={key}
            value={(currentTestStep as any)[key] || ""}
            onChange={(e) => setCurrentTestStep((prev) => (prev ? { ...prev, [key]: e.target.value } : null))}
            fullWidth
            margin="normal"
            size="small"
            disabled={key === "stepId"}
            required={["action", "failOnError"].includes(key)}
            error={["action", "failOnError"].includes(key) && !(currentTestStep as any)[key]}
            helperText={["action", "failOnError"].includes(key) && !(currentTestStep as any)[key] && `${key} is required`}
          />
        ))} */}

        {/* Dataset ID Dropdown */}
        {/* <FormControl fullWidth margin="normal">
          <Autocomplete size="small" options={dataSets} 
              getOptionLabel={(option) => String(option?.dataSetId)}
              value={dataSets.find((dataSet) => dataSet.dataSetId === currentTestStep?.dataSetId) || null}
              onChange={(_, newValue) => setCurrentTestStep((prev) => (prev ? { ...prev, "dataSetId": newValue?.dataSetId } : null))}
              renderInput={(params) => (
              <TextField {...params} label="Select DataSet" />)}
          />
        </FormControl>

        
      </DialogContent>
      <DialogActions>
        <Button sx={{ backgroundColor: "primary.light" }} onClick={closeDialog} startIcon={<Cancel />}>
          Cancel
        </Button>
        <Button onClick={saveTestStep} startIcon={<Save />} variant="contained" disabled={!currentTestStep?.dataSetId || !currentTestStep?.dataSetId}>
          Save
        </Button>
      </DialogActions>
    </Dialog> */}
    </Box>
  );
};
